import { calculateDaysDifference, dateFromPartial, partialDateFromDate, partialDateToValue } from 'neuro-utils';
import { formatPartialDate } from 'Utility/date';
import { getPlatformInviteFlavorString, matchesInviteFlavorString } from 'Routes/MyService/util';
import { fetchWithOptions } from 'Utility/fetch';
import { getJWTData, parseJWTFromCookie } from 'Utility/jwtAuthTools';
import { localStorageGet } from 'Utility/localStorage';
import { ActionableErrorContext, Loggable, makeLog } from 'Utility/logger';
import { sortAndMergeDocuments } from 'Utility/documentHandling';
import { TTaskRecurrence, TTaskV3 } from '@stellarq/tasks';

export type TPatientDevice = {
  deviceId: string;
  userId: string;
  deviceModel: string;
  jwtId: string;
  expirationTime: number;
};

/**
 * Get array of devices for patient
 * @param {IPatientAPI['id']} patientId - Patient id
 * @returns {Promise<Array<TPatientDevice>>} Promise array of the devices for patient or empty array
 */
export const getPatientDevices = (patientId: IPatientAPI['id']): Promise<Array<TPatientDevice>> =>
  fetchWithOptions(`/api/devices/${patientId}`, { neurojwt: parseJWTFromCookie() }, { method: 'GET' })
    .then((res: Response) => {
      if (res.status === 200) return res.json();
      else return [];
    })
    .catch((error: Error) => {
      makeLog(Loggable.BugActionable, error, new ActionableErrorContext(null, 'GET patient devices'));
    });

const getDayString = (jsDay: number): string | undefined => {
  switch (jsDay) {
    case 0: {
      return 'Sun';
    }
    case 1: {
      return 'Mon';
    }
    case 2: {
      return 'Tue';
    }
    case 3: {
      return 'Wed';
    }
    case 4: {
      return 'Thu';
    }
    case 5: {
      return 'Fri';
    }
    case 6: {
      return 'Sat';
    }
    default: {
      return undefined;
    }
  }
};

const formatBody = (taskList: ITaskList, platform: string, orgId: string, patientId: string): TTaskV3 => {
  let cron: string = '';
  let everyNth: number = 1;
  if (taskList.recurring && taskList.sendDate) {
    const sendDateDay = dateFromPartial(taskList.sendDate).getDay();
    const sendDateDayString = getDayString(sendDateDay);
    cron = `0 0 0 * * ${sendDateDayString}`;
    if (taskList.weeklySurveyInterval === 'everyOtherWeek') {
      everyNth = 2;
    } else if (taskList.weeklySurveyInterval === 'everyFourthWeek') {
      everyNth = 4;
    }
  }

  const dayMS = 86400000;
  const weekMS = 604800000;
  const taskDurationMax =
    taskList.weeklySurveyInterval === 'weekly'
      ? weekMS
      : taskList.weeklySurveyInterval === 'everyOtherWeek'
        ? 2 * weekMS
        : taskList.weeklySurveyInterval === 'everyFourthWeek'
          ? 4 * weekMS
          : undefined;

  const recurringDatesDifference =
    partialDateToValue(taskList.recurringTaskDeadline) - partialDateToValue(taskList.sendDate);

  const recurringDurationDays =
    taskDurationMax && recurringDatesDifference > taskDurationMax
      ? taskDurationMax / dayMS
      : (calculateDaysDifference(taskList.sendDate, taskList.recurringTaskDeadline) ?? 0) + 1;

  const taskEndTimeDate = taskList.end_time ? new Date(taskList.end_time) : undefined;
  const isMidnight =
    taskEndTimeDate?.getHours() === 0 && taskEndTimeDate?.getMinutes() === 0 && taskEndTimeDate?.getSeconds() === 0;
  const isCloseable =
    taskList.tasks?.includes('treatmentEfficiencyAfter') || taskList.tasks?.includes('treatmentEfficiencyBefore');
  const usableEndTime =
    taskEndTimeDate && !isMidnight && (isCloseable || taskList.appointmentId) ? taskEndTimeDate : undefined;

  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  return {
    task_kind: taskList.informantId ? 'IROM' : 'PROM',
    task_id: taskList.id,
    issuer_org_id: orgId,
    patient_id: patientId,
    informant_id: taskList.informantId,
    timezone,
    start_inclusive: `${formatPartialDate(taskList.sendDate)} 00:00:00`,
    end_exclusive: usableEndTime
      ? `${formatPartialDate(partialDateFromDate(usableEndTime))} ${usableEndTime.toTimeString().slice(0, 8)}`
      : taskList.deadline
        ? `${formatPartialDate(partialDateFromDate(new Date(partialDateToValue(taskList.deadline) + dayMS)))} 00:00:00`
        : null,
    recurrence: taskList.recurring
      ? { cron, every_nth: everyNth, duration_cal_days: recurringDurationDays ?? 1 }
      : null,
    actions_required: taskList.tasks?.map(task => ({ relative_priority: 0, action_identifier: task })) || [],
    reminders_before_end: taskList.reminders_before_end ?? [],
    alerts_after_start: taskList.alerts_after_start ?? [],
    removed_at: null,
    context: {
      neuro_platform_id: platform,
      appointment_id: taskList.appointmentId,
      template_id: taskList.title ?? '',
      message_localized: taskList.description ?? '',
    },
  };
};

/**
 * Post tasklist
 * @param {ITaskList} taskList data to post
 */
export const sendTaskList = async (taskList: ITaskList): Promise<TTaskV3 | null> => {
  const patientid = getJWTData()?.patientid || null;
  const orgid = getJWTData()?.orgid || null;
  const platform = localStorageGet('platform');

  if (!patientid || !orgid || !platform) return null;

  return fetchWithOptions(
    '/api/patient/v3/task',
    { neurojwt: parseJWTFromCookie() },
    {
      method: 'POST',
      body: JSON.stringify(formatBody(taskList, platform, orgid, patientid)),
    },
  )
    .then(res => {
      if (res.status === 200) return res.json();
      throw new Error('unexpected status ' + res.status);
    })
    .catch((error: Error) => {
      makeLog(Loggable.BugActionable, error, new ActionableErrorContext(null, 'POST task'));
      return false;
    });
};

/**
 * Update tasklist
 * @param {ITaskList} taskList data to post
 */
export const updateTaskListFetcher = async (taskList: ITaskList): Promise<TTaskV3 | null> => {
  const patientid = getJWTData()?.patientid || null;
  const orgid = getJWTData()?.orgid || null;
  const platform = localStorageGet('platform');

  if (!patientid || !orgid || !platform) return null;

  return fetchWithOptions(
    `/api/patient/v3/task/${taskList.id}`,
    { neurojwt: parseJWTFromCookie() },
    {
      method: 'PATCH',
      body: JSON.stringify(formatBody(taskList, platform, orgid, patientid)),
    },
  )
    .then(res => {
      if (res.status === 200) return res.json();
      throw new Error('unexpected status ' + res.status);
    })
    .catch((error: Error) => {
      makeLog(Loggable.BugActionable, error, new ActionableErrorContext(null, 'update task'));
      return null;
    });
};

/**
 * Delete tasklist
 * @param {ITaskList} taskList - Tasklist to be deleted
 */
export const deleteTaskListFetcher = async (taskList: ITaskList): Promise<boolean | null> => {
  return fetchWithOptions(
    `/api/patient/v3/task/${taskList.id}`,
    { neurojwt: parseJWTFromCookie() },
    {
      method: 'DELETE',
    },
  )
    .then(res => {
      if (res.ok) return true;
      throw new Error('unexpected status ' + res.status);
    })
    .catch((error: Error) => {
      makeLog(Loggable.BugActionable, error, new ActionableErrorContext(null, 'DELETE task'));
      return false;
    });
};

/**
 *
 * @param customCron tasklists repeat_schedule field from backend in format of TTaskRecurrence form @stellarq/tasks
 * @returns {ITaskList['weeklySurveyInterval']}
 */
const cronToWeeklyInterval = (customCron: TTaskRecurrence): ITaskList['weeklySurveyInterval'] => {
  const cron = customCron.cron;
  const everyNth = customCron.every_nth;
  if (!cron) return;
  if (everyNth === 2) {
    return 'everyOtherWeek';
  } else if (everyNth === 4) {
    return 'everyFourthWeek';
  } else return 'weekly';
};

export const getTaskLists = async (): Promise<Array<ITaskList> | null> => {
  return fetchWithOptions(
    '/api/patient/v3/task?expired=true&IROM=true',
    { neurojwt: parseJWTFromCookie() },
    { method: 'GET' },
  )
    .then((res: Response) => {
      if (res.status === 200) return res.json();
      throw new Error('unexpected status ' + res.status);
    })
    .then((data: TTaskV3[]) => {
      if (data.length > 0) {
        return data.map<ITaskList>((d: TTaskV3) => {
          return {
            id: d.task_id,
            deadline: partialDateFromDate(
              d.end_exclusive ? new Date(new Date(d.end_exclusive).getTime() - 1) : new Date(),
            ),
            tasks: d.actions_required
              .toSorted((a1, a2) => a1.relative_priority - a2.relative_priority)
              .map(task => task.action_identifier) as NonNullable<ITaskList['tasks']>,
            weeklySurveyInterval: d.recurrence ? cronToWeeklyInterval(d.recurrence) : undefined,
            sendDate: partialDateFromDate(new Date(d.start_inclusive)),
            description: d.context.message_localized,
            title: d.context.template_id,
            recurring: !!d.recurrence,
            recurringTaskDeadline: d.recurrence
              ? partialDateFromDate(
                  new Date(new Date(d.start_inclusive).getTime() + (d?.recurrence?.duration_cal_days ?? 0) * 86400000),
                )
              : undefined,
            isEditing: false,
            start_time: new Date(d.start_inclusive).getTime(),
            end_time: d.end_exclusive ? new Date(d.end_exclusive).getTime() : undefined,
            task_duration: d.recurrence?.duration_cal_days ? d.recurrence.duration_cal_days * 86400000 : null,
            repeat_schedule: d.recurrence ? `${d.recurrence.cron}:every-nth:${d.recurrence.every_nth}` : null,
            reminders_before_end: d.reminders_before_end,
            alerts_after_start: d.alerts_after_start,
            appointmentId: d.context.appointment_id ?? undefined,
            task_kind: d.task_kind,
            informantId: d.informant_id ?? undefined,
          };
        });
      } else return [];
    })
    .catch((error: Error) => {
      makeLog(Loggable.BugActionable, error, new ActionableErrorContext(null, 'GET tasks'));
      return null;
    });
};

/**
 * Send Invite to use MyService
 * @param {string} carrier email address or phone number
 * @param {string} medium  'EMAIL' | 'SMS'
 * @param {string} orgId organization ID
 * @param {Locale} lang - 2-letter ISO 639 code of the invite recipient language
 */
export const sendMyInvite = (carrier: string, medium: string, orgId: string, lang: Locale) =>
  fetchWithOptions(
    '/api/patient/v2/invite/',
    { neurojwt: parseJWTFromCookie() },
    {
      method: 'POST',
      body: JSON.stringify({
        carrier: carrier,
        medium: medium,
        flavor: getPlatformInviteFlavorString(),
        org_id: orgId,
        locale: lang,
      }),
    },
  );

/**
 * Create new document with finished commit
 * @param {string} documentType documentType
 * @param {IControlProps} docData data of the document created
 * @returns object with documentId and commitId if creation was succesful
 */
export const createNewDocumentWithCommit = async (
  documentType: string,
  docData: TAnyObject,
): Promise<{ documentId: string; commitId: string } | null> => {
  return fetchWithOptions(
    `/api/mydocuments/${documentType}`,
    { neurojwt: parseJWTFromCookie() },
    { method: 'PUT', body: JSON.stringify(docData) },
  )
    .then((res: Response) => {
      if (res.status === 200) {
        return res.json();
      } else {
        throw new Error('unexpected status ' + res.status);
      }
    })
    .catch((error: Error) => {
      makeLog(Loggable.BugActionable, error, new ActionableErrorContext(null, 'PUT document MySQ'));
      return null;
    });
};

/**
 * Create a finished commit to a document
 * @param {string} documentType documentType
 * @param {string} documentId docId
 * @param {TAnyObject} commitData data of the commit
 * @returns Object with the new commitId if the creation was succesful
 */
export const createCommitToDocument = async (
  documentType: string,
  documentId: string,
  commitData: TAnyObject,
): Promise<{ commitId: string } | null> => {
  return fetchWithOptions(
    `/api/mydocuments/${documentType}/${documentId}`,
    { neurojwt: parseJWTFromCookie() },
    { method: 'PUT', body: JSON.stringify(commitData) },
  )
    .then((res: Response) => {
      if (res.status === 200) {
        return res.json();
      } else {
        throw new Error('unexpected status ' + res.status);
      }
    })
    .catch((error: Error) => {
      makeLog(Loggable.BugActionable, error, new ActionableErrorContext(null, 'PUT commit MySQ'));
      return null;
    });
};

/**
 * Delete document
 * @param {string} documentType documentType
 * @param {string} documentId docId
 * @returns Document deletion success
 */
export const deleteDocumentFetcher = async (
  documentType: string,
  documentId: string,
  reason: Record<string, string>,
): Promise<boolean> => {
  return fetchWithOptions(
    `/api/mydocuments/${documentType}/${documentId}`,
    { neurojwt: parseJWTFromCookie() },
    { method: 'DELETE', body: JSON.stringify({ reason, date: Date.now() }) },
  )
    .then((res: Response) => {
      if (res.status === 200) {
        return true;
      } else {
        throw new Error('unexpected status ' + res.status);
      }
    })
    .catch((error: Error) => {
      makeLog(Loggable.BugActionable, error, new ActionableErrorContext(null, 'DELETE document MySQ'));
      return false;
    });
};

/**
 * Fetch invites (to become a MySQ user) sent to the patient (associated with
 * the current session).
 */
export async function getMyAppInvites<Invite extends { flavor: string }>(): Promise<Invite[]> {
  try {
    const res = await fetchWithOptions(
      '/api/patient/v2/stimulus', // API ~ neuro-api:0.19.4, neuro-patient-service:0.28.1
      { neurojwt: parseJWTFromCookie() },
      { method: 'GET' },
    );
    if (res.status === 200) {
      return (await res.json()).filter((s: TNeuroStimulus) => matchesInviteFlavorString(s.flavor));
    } else return [];
  } catch (err) {
    makeLog(
      Loggable.BugActionable,
      err instanceof Error ? err : new Error('strange error'),
      new ActionableErrorContext(null, 'GET sent invites log'),
    );
    return [];
  }
}

/**
 * Fetch MySQ user identity of the patient (associated with the current session).
 */
export async function getMyAppUserIdentity(): Promise<string | null> {
  try {
    const res = await fetchWithOptions(
      '/api/vault-mysq/v2/user/ssn',
      { neurojwt: parseJWTFromCookie() },
      { method: 'GET' },
    );
    if (res.status === 200) {
      const body: { userId: string } | null = await res.json();
      if (typeof body?.userId === 'string') {
        return body.userId;
      } else {
        return null;
      }
    } else {
      return null;
    }
  } catch (err) {
    makeLog(
      Loggable.BugActionable,
      err instanceof Error ? err : new Error('strange error'),
      new ActionableErrorContext(null, 'GET user identity MySQ'),
    );
    return null;
  }
}

/**
 * Get "My Service" data docs of the patient (associated with the current
 * session).
 */
export async function getMyAppDocs(): Promise<TMergerReturnType[]> {
  try {
    const res = await fetchWithOptions(
      '/api/mydocuments', // API ~ neuro-api:0.19.4
      { neurojwt: parseJWTFromCookie() },
      { method: 'GET' },
    );
    if (res.status === 200) {
      const docs = await res.json();
      return sortAndMergeDocuments(docs);
    } else return [];
  } catch (err) {
    makeLog(
      Loggable.BugActionable,
      err instanceof Error ? err : new Error('strange error'),
      new ActionableErrorContext(null, 'GET documents MySQ'),
    );
    return [];
  }
}

type TMyUserContactResponse = {
  userId: string;
  contactType: string;
  contactValue: Array<string>;
  isContactVerified: boolean;
};

export const fetchMyUserContactInfo = async (userId: string): Promise<Array<TMyUserContactResponse> | null> => {
  return fetchWithOptions(
    `/api/vault-mysq/v2/informant/contacts/${userId}`,
    { neurojwt: parseJWTFromCookie() },
    { method: 'GET' },
  )
    .then(res => {
      if (res.status === 200) return res.json() as unknown as Array<TMyUserContactResponse>;
      else throw new Error('unexpected status ' + res.status);
    })
    .catch(e => {
      makeLog(Loggable.BugActionable, e, new ActionableErrorContext(null, 'GET mysq user contact info'));
      return null;
    });
};

type TRespUserData = {
  userId: string;
  organization: string;
  firstNames: string;
  lastNames: string;
  active: boolean;
  uILanguage: string;
};

export const fetchMyUserData = async (userId: string): Promise<TRespUserData | null> => {
  return fetchWithOptions(
    `/api/vault-mysq/v2/informant/user/${userId}`,
    { neurojwt: parseJWTFromCookie() },
    { method: 'GET' },
  )
    .then(res => {
      if (res.status === 200) return res.json() as unknown as TRespUserData;
      else throw new Error('unexpected status ' + res.status);
    })
    .catch(e => {
      makeLog(Loggable.BugActionable, e, new ActionableErrorContext(null, 'GET mysq user data'));
      return null;
    });
};

export const fetchMyUserDataBySsn = async (ssn: string, ssnLocalization: string): Promise<TRespUserData | null> => {
  return fetchWithOptions(
    `/api/vault-mysq/v2/user/ssn/${ssnLocalization}/${ssn}`,
    { neurojwt: parseJWTFromCookie() },
    { method: 'GET' },
  )
    .then(res => {
      if (res.status === 200) return res.json() as unknown as TRespUserData;
      if (res.status === 204) return null;
      else throw new Error('unexpected status ' + res.status);
    })
    .catch(e => {
      makeLog(Loggable.BugActionable, e, new ActionableErrorContext(null, 'GET mysq user data by ssn'));
      return null;
    });
};
